import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { Badge } from "@/components/ui/badge";
import type { Registration, AgeGroup, PaymentStatus } from "@/pages/hub/HubTournamentDemo";

interface Tournament {
  fees: Record<string, { amount: number; surface: string }>;
  depositAmount: number;
}

interface Props {
  tournament: Tournament;
  academies: string[];
  registrations: Registration[];
  onAdd: (entry: Omit<Registration, "id">) => void;
}

const schema = z.object({
  academy: z.string().min(1, "Select an academy"),
  ageGroup: z.enum(["U11", "U13", "U15", "U17"]),
  teamName: z.string().min(2, "Enter a team name"),
  coachName: z.string().min(2, "Enter coach name"),
  coachEmail: z.string().email("Enter a valid email"),
  coachPhone: z.string().min(7, "Enter a phone number"),
  playerCount: z.coerce.number().int().min(1, "Enter player count"),
});

type FormValues = z.infer<typeof schema>;

export function TeamRegistrationTab({ tournament, academies, registrations, onAdd }: Props) {
  const { toast } = useToast();

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      academy: "",
      ageGroup: "U11",
      teamName: "",
      coachName: "",
      coachEmail: "",
      coachPhone: "",
      playerCount: 12,
    },
  });

  const selectedAge = form.watch("ageGroup") as AgeGroup;
  const feeInfo = tournament.fees[selectedAge];

  function onSubmit(values: FormValues) {
    onAdd({
      academy: values.academy,
      ageGroup: values.ageGroup as AgeGroup,
      teamName: values.teamName,
      coachName: values.coachName,
      coachEmail: values.coachEmail,
      coachPhone: values.coachPhone,
      playerCount: values.playerCount,
      amountDue: feeInfo.amount,
      paid: 0,
      status: "unpaid" as PaymentStatus,
    });

    toast({
      title: "Registered!",
      description: `${values.teamName} has been registered. Awaiting payment.`,
    });

    form.reset();
  }

  const recent = [...registrations].reverse().slice(0, 5);

  return (
    <div className="space-y-6" data-testid="team-registration-tab">
      <div className="grid md:grid-cols-2 gap-6">
        {/* Registration form */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Register a Team</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              {/* Academy */}
              <div>
                <Label>Academy</Label>
                <Select
                  onValueChange={(v) => form.setValue("academy", v, { shouldValidate: true })}
                  value={form.watch("academy")}
                >
                  <SelectTrigger className="mt-1" data-testid="select-academy">
                    <SelectValue placeholder="Select academy…" />
                  </SelectTrigger>
                  <SelectContent>
                    {academies.map((a) => (
                      <SelectItem key={a} value={a}>
                        {a}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {form.formState.errors.academy && (
                  <p className="text-xs text-red-500 mt-1">
                    {form.formState.errors.academy.message}
                  </p>
                )}
              </div>

              {/* Age group */}
              <div>
                <Label className="mb-2 block">Age Group</Label>
                <RadioGroup
                  value={selectedAge}
                  onValueChange={(v) => form.setValue("ageGroup", v as AgeGroup)}
                  className="flex gap-4 flex-wrap"
                  data-testid="radio-age-group"
                >
                  {(["U11", "U13", "U15", "U17"] as AgeGroup[]).map((ag) => (
                    <div key={ag} className="flex items-center gap-1.5">
                      <RadioGroupItem value={ag} id={`age-${ag}`} />
                      <Label htmlFor={`age-${ag}`} className="cursor-pointer font-normal">
                        {ag}
                      </Label>
                    </div>
                  ))}
                </RadioGroup>
              </div>

              {/* Team name */}
              <div>
                <Label htmlFor="team-name">Team Name</Label>
                <Input
                  id="team-name"
                  className="mt-1"
                  data-testid="input-team-name"
                  {...form.register("teamName")}
                />
                {form.formState.errors.teamName && (
                  <p className="text-xs text-red-500 mt-1">
                    {form.formState.errors.teamName.message}
                  </p>
                )}
              </div>

              {/* Coach */}
              <div className="grid grid-cols-1 gap-3">
                <div>
                  <Label htmlFor="coach-name">Coach Name</Label>
                  <Input id="coach-name" className="mt-1" {...form.register("coachName")} />
                  {form.formState.errors.coachName && (
                    <p className="text-xs text-red-500 mt-1">
                      {form.formState.errors.coachName.message}
                    </p>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="coach-email">Email</Label>
                    <Input
                      id="coach-email"
                      type="email"
                      className="mt-1"
                      {...form.register("coachEmail")}
                    />
                    {form.formState.errors.coachEmail && (
                      <p className="text-xs text-red-500 mt-1">
                        {form.formState.errors.coachEmail.message}
                      </p>
                    )}
                  </div>
                  <div>
                    <Label htmlFor="coach-phone">Phone</Label>
                    <Input
                      id="coach-phone"
                      className="mt-1"
                      {...form.register("coachPhone")}
                    />
                  </div>
                </div>
              </div>

              {/* Player count */}
              <div>
                <Label htmlFor="player-count">Expected Player Count</Label>
                <Input
                  id="player-count"
                  type="number"
                  min={1}
                  className="mt-1 w-32"
                  {...form.register("playerCount")}
                />
              </div>

              {/* Live fee calc */}
              <div className="rounded-lg bg-blue-50 border border-blue-100 px-4 py-3">
                <p className="text-sm text-blue-800">
                  <span className="font-semibold">
                    Registration fee: ${feeInfo.amount}
                  </span>{" "}
                  <span className="text-blue-600">({feeInfo.surface})</span>
                </p>
                <p className="text-xs text-blue-500 mt-0.5">
                  Deposit: ${tournament.depositAmount} due first
                </p>
              </div>

              <Button
                type="submit"
                className="w-full bg-[#1E2761] hover:bg-[#2d3a8c] text-white"
                data-testid="btn-register-team"
              >
                Register Team
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Recently registered */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Recently Registered</CardTitle>
          </CardHeader>
          <CardContent>
            {recent.length === 0 ? (
              <div className="py-12 text-center text-gray-400">
                <p className="text-sm">No teams registered yet.</p>
                <p className="text-xs mt-1">Registrations will appear here.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {recent.map((r) => (
                  <div
                    key={r.id}
                    className="flex items-start justify-between py-2 border-b border-gray-100 last:border-0"
                  >
                    <div>
                      <p className="text-sm font-medium text-gray-900">{r.teamName}</p>
                      <p className="text-xs text-gray-500">{r.coachName} · {r.academy}</p>
                    </div>
                    <Badge variant="secondary" className="shrink-0 ml-2">
                      {r.ageGroup}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
